import { useMemo } from 'react';

import { useGetOptionsForCLA } from 'hooks/useGetOptionsForCLA';
import { TFunction } from 'i18next';
import { useGetBonus25Methods } from 'queries';
import { useTranslation } from 'react-i18next';

import { Bonus25Method } from '../../../types';

import { singleSelectValueFormatter } from 'components/shared/DataGrid/utils/valueFormatters/singleSelectValueFormatter';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';
import DateCell from 'components/shared/Table/TableCell/DataGrid/DateCell';
import LocalizedTimeCell from 'components/shared/Table/TableCell/DataGrid/LocalizedTimeCell';

import { PremiumPaysTableData } from './useGetPremiumPaysTableData';

const useColumnDefinitions = (): GridColDef[] => {
  const { t } = useTranslation('common');

  const premiumPayTypes = useGetOptionsForCLA();
  const { data: bonus25Methods } = useGetBonus25Methods();

  return useMemo(() => {
    return columnDefinitions(t, premiumPayTypes, bonus25Methods);
  }, [t, premiumPayTypes, bonus25Methods]);
};

const columnDefinitions = (
  t: TFunction,
  premiumPayTypes: Option[],
  bonus25Methods: Bonus25Method[] = []
): GridColDef[] => [
  {
    field: 'claBonusId',
    flex: 1,
    headerName: t('premiumPays.grid.claBonusId'),
    type: 'hidden'
  },
  {
    field: 'claId',
    flex: 1,
    headerName: t('premiumPays.grid.claId'),
    hideable: false,
    width: 50,
    valueOptions: premiumPayTypes,
    type: 'singleSelect',
    valueFormatter: (params) =>
      singleSelectValueFormatter(params, premiumPayTypes),
    renderCell: (params: GridRenderCellParams<Date>) => params.formattedValue
  },
  {
    field: 'columnNr',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    headerName: t('premiumPays.grid.columnNr'),
    type: 'number',
    width: 30
  },
  {
    field: 'bonusTypeSvcName',
    flex: 1,
    minWidth: 170,
    headerName: t('premiumPays.grid.bonusTypeSvc'),
    type: 'string'
  },
  {
    field: 'percentage',
    flex: 1,
    width: 60,
    headerAlign: 'left',
    align: 'left',
    headerName: t('premiumPays.grid.percentage'),
    type: 'number'
  },
  {
    field: 'timeFrom',
    flex: 1,
    minWidth: 80,
    headerName: t('premiumPays.grid.timeFrom'),
    type: 'date',
    renderCell: (
      params: GridRenderCellParams<PremiumPaysTableData['timeFrom']>
    ) => <LocalizedTimeCell dateTime={params.value} />
  },
  {
    field: 'timeTo',
    flex: 1,
    minWidth: 80,
    headerName: t('premiumPays.grid.timeTo'),
    type: 'date',
    renderCell: (
      params: GridRenderCellParams<PremiumPaysTableData['timeTo']>
    ) => <LocalizedTimeCell dateTime={params.value} />
  },
  {
    field: 'hasDayMonday',
    flex: 1,
    headerName: t('weekday.monday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayTuesday',
    flex: 1,
    headerName: t('weekday.tuesday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayWednesday',
    flex: 1,
    headerName: t('weekday.wednesday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayThursday',
    flex: 1,
    headerName: t('weekday.thursday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayFriday',
    flex: 1,
    headerName: t('weekday.friday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDaySaturday',
    flex: 1,
    headerName: t('weekday.saturday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDaySunday',
    flex: 1,
    headerName: t('weekday.sunday'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayHoliday',
    flex: 1,
    headerName: t('premiumPays.grid.flags.DAY_HOLIDAY'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayHolidaySat',
    flex: 1,
    headerName: t('premiumPays.grid.flags.DAY_HOLIDAY_SAT'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDayHolidaySun',
    flex: 1,
    headerName: t('premiumPays.grid.flags.DAY_HOLIDAY_SUN'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasDaySpecial',
    flex: 1,
    headerName: t('premiumPays.grid.flags.DAY_SPECIAL'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'hasTimeFromTHR',
    flex: 1,
    headerName: t('premiumPays.grid.flags.TIME_FROM_THR'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'timeFromThr',
    flex: 1,
    headerName: t('premiumPays.grid.timeFromThr'),
    type: 'date',
    renderCell: (
      params: GridRenderCellParams<PremiumPaysTableData['timeFromThr']>
    ) => <LocalizedTimeCell dateTime={params.value} />
  },
  {
    field: 'hasTimeToTHR',
    flex: 1,
    headerName: t('premiumPays.grid.flags.TIME_TO_THR'),
    type: 'boolean',
    renderCell: (params) => <BooleanCell value={params.value} />
  },
  {
    field: 'timeToThr',
    flex: 1,
    headerName: t('premiumPays.grid.timeToThr'),
    type: 'date',
    renderCell: (
      params: GridRenderCellParams<PremiumPaysTableData['timeToThr']>
    ) => <LocalizedTimeCell dateTime={params.value} />
  },
  {
    field: 'maxHoursPerDayrange',
    flex: 1,
    headerAlign: 'left',
    headerName: t('premiumPays.grid.maxHoursPerDayrange'),
    type: 'number'
  },
  {
    field: 'dayrange',
    flex: 1,
    headerAlign: 'left',
    headerName: t('premiumPays.grid.dayrange'),
    type: 'number'
  },
  {
    field: 'validFrom',
    flex: 1,
    minWidth: 110,
    headerName: t('validFrom'),
    type: 'date',
    renderCell: (
      params: GridRenderCellParams<PremiumPaysTableData['validFrom']>
    ) => {
      return <DateCell value={params.value} />;
    }
  },
  {
    field: 'validTo',
    flex: 1,
    minWidth: 110,
    headerName: t('validTo'),
    type: 'date',
    renderCell: (
      params: GridRenderCellParams<PremiumPaysTableData['validTo']>
    ) => {
      return <DateCell value={params.value} />;
    }
  },
  {
    field: 'bonus25Method',
    flex: 1,
    headerName: t('premiumPays.grid.bonus25Method'),
    type: 'string',
    minWidth: 140,
    valueFormatter: (params) =>
      bonus25Methods.length
        ? bonus25Methods.filter(
            (bonus25Method) => bonus25Method.code === params.value
          )[0]?.name
        : ''
  },
  {
    field: 'scPri',
    flex: 1,
    headerName: t('premiumPays.grid.scPri'),
    type: 'string'
  },
  {
    field: 'scSec',
    flex: 1,
    headerName: t('premiumPays.grid.scSec'),
    type: 'string'
  }
];

export default useColumnDefinitions;
